import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import { cx } from '@amumin/ui-core';

import { createDateCommit, type DateCommit } from './dateCommit.js';
import styles from './DateTimeField.module.css';
import field from './TextField.module.css';

// Datum- en tijdvelden (verzoek oprichter 2026-09-24: "datum en tijden lopen niet lekker"). Het
// datumveld is de native <input type="date">: nooit afgekapte waarden, het kalendericoon staat
// naast de tekst (niet eroverheen), tabelcijfers, dezelfde rand en hoogte als TextField. Het
// tijdveld heeft een eigen kiezer (TimeField.tsx). Leg ze naast elkaar met FieldPair: die stapelt
// vanzelf als er geen ruimte is.

interface TemporalProps {
  id: string;
  label: string;
  value: string;
  onInput: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  name?: string;
  /** Label alleen voor schermlezers (bv. in een lijstrij met de naam ernaast). */
  hideLabel?: boolean;
  /** Zonder ruimte eronder (in een rij of lijst). */
  compact?: boolean;
  /**
   * Direct opslaan pas na kiezen: een kalenderkeuze gaat meteen door, getypte cijfers pas bij
   * verlaten van het veld of Enter (nooit halverwege het jaartal; zie dateCommit.ts).
   */
  commitOnChange?: boolean;
}

export type DateFieldProps = TemporalProps;

function TemporalField({
  id,
  label,
  value,
  onInput,
  error,
  hint,
  required,
  disabled,
  min,
  max,
  name,
  hideLabel,
  compact,
  commitOnChange,
}: TemporalProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  const emit = (e: Event) => onInput((e.currentTarget as HTMLInputElement).value);
  // Laatste onInput via een ref, zodat de commit-logica één keer per veld bestaat.
  const latest = useRef(onInput);
  latest.current = onInput;
  const commit = useRef<DateCommit | null>(null);
  if (!commit.current) commit.current = createDateCommit(value, (v) => latest.current(v));
  useEffect(() => commit.current?.sync(value), [value]);
  const el = (e: Event) => e.currentTarget as HTMLInputElement;
  const incomplete = (i: HTMLInputElement) => Boolean(i.validity && i.validity.badInput);
  const committing = {
    onChange: (e: Event) => commit.current?.change(el(e).value),
    onKeyDown: (e: KeyboardEvent) => commit.current?.keydown(e.key, el(e).value, incomplete(el(e))),
    onBlur: (e: Event) => commit.current?.blur(el(e).value, incomplete(el(e))),
  };
  return (
    <div class={cx(field.field, styles.date)} style={compact ? 'margin-bottom: 0' : undefined}>
      <label class={hideLabel ? 'fb-sr-only' : field.label} for={id}>
        {label}
      </label>
      <input
        id={id}
        name={name ?? id}
        class={cx(field.input, styles.input)}
        type="date"
        value={value}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        data-temporal="date"
        {...(commitOnChange ? committing : { onInput: emit })}
      />
      {error ? (
        <span id={`${id}-error`} class={field.error} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={`${id}-hint`} class={field.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** Datumveld (native kalender); breed genoeg voor "24-09-2026" plus het kalendericoon. */
export function DateField(props: DateFieldProps) {
  return <TemporalField {...props} />;
}

export interface FieldPairProps {
  children: ComponentChildren;
  /** Minimale kolombreedte in px; smaller dan twee kolommen → onder elkaar. Standaard 140. */
  min?: number;
  name?: string;
}

/** Twee (of meer) velden naast elkaar, gelijke breedte; stapelt als ze niet passen. */
export function FieldPair({ children, min = 140, name }: FieldPairProps) {
  return (
    <div class={styles.pair} style={`--pair-min: ${min}px`} data-name={name}>
      {children}
    </div>
  );
}
